import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from "react-toastify";

//import components
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchVehiclesData } from "../../store/vehicles/actions";

const VehiclesTable = props => {
    const dispatch = useDispatch();
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [updatingVehicleId, setUpdatingVehicleId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedTypeId, setSelectedTypeId] = useState('');

    const { data, loading, error } = useSelector(state => state.Vehicles);

    const { id: vehicleId } = useParams();
    
    useEffect(() => {
        dispatch(fetchVehiclesData(vehicleId));
    }, [dispatch, vehicleId]);

    // Récupérer les types de véhicules disponibles
    useEffect(() => {
        const fetchVehicleTypes = async () => {
            try {
                const token = localStorage.getItem("authToken");
                // S'assurer que l'URL est absolue
                const baseUrl = process.env.REACT_APP_WEB_APP_URL || '';
                const apiUrl = baseUrl + "/api/web/vehicle-types";
                
                // Vérifier que l'URL est absolue
                if (!apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
                    console.error('Invalid API URL (not absolute):', apiUrl);
                    return;
                }
                
                console.log('Fetching vehicle types from:', apiUrl);
                
                const response = await fetch(
                    apiUrl,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                const result = await response.json();
                
                // Gérer différentes structures de réponse
                let types = [];
                if (response.status === 200) {
                    if (result.data && Array.isArray(result.data)) {
                        types = result.data;
                    } else if (result.data && result.data.data && Array.isArray(result.data.data)) {
                        types = result.data.data;
                    } else if (Array.isArray(result)) {
                        types = result;
                    }
                    
                    // Filtrer seulement les types activés (isenable = 1)
                    types = types.filter(type => type.isenable === 1 || type.isenable === "1" || type.enable === "Yes");
                    
                    if (types.length > 0) {
                        setVehicleTypes(types);
                    }
                }
            } catch (err) {
                console.error("Error fetching vehicle types:", err);
            }
        };
        fetchVehicleTypes();
    }, []);


    const handleUpdateVehicleType = useCallback(async (vehicleIdToUpdate, typeId) => {
        if (!typeId) {
            toast.error('Please select a vehicle type', { autoClose: 3000 });
            return;
        }

        setUpdatingVehicleId(vehicleIdToUpdate);
        
        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(
                process.env.REACT_APP_WEB_APP_URL + "/api/web/drivers/update-vehicle-type",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        vehicle_id: vehicleIdToUpdate,
                        type_id: typeId,
                    }),
                }
            );

            const result = await response.json();

            if (response.status === 200 && result.success === "1") {
                toast.success(result.message || 'Vehicle type updated successfully', { autoClose: 3000 });
                setModalOpen(false);
                setSelectedVehicle(null);
                setSelectedTypeId('');
                // Rafraîchir les données - vehicleId vient de useParams() et représente l'ID du driver
                // Attendre un court délai pour s'assurer que la base de données est à jour
                setTimeout(() => {
                    dispatch(fetchVehiclesData(vehicleId));
                }, 300);
            } else {
                toast.error(result?.message || 'Failed to update vehicle type', { autoClose: 3000 });
            }
        } catch (err) {
            console.error("Error updating vehicle type:", err);
            toast.error("Failed to update vehicle type", { autoClose: 3000 });
        } finally {
            setUpdatingVehicleId(null);
        }
    }, [dispatch, vehicleId]);

    const handleModalSave = () => {
        if (selectedVehicle && selectedTypeId) {
            handleUpdateVehicleType(selectedVehicle.id, selectedTypeId);
        } else {
            toast.error('Please select a vehicle type', { autoClose: 3000 });
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (!modalOpen) {
            setSelectedVehicle(null);
            setSelectedTypeId('');
        }
    };

    const columns = useMemo(
        () => [
          {
            header: "Image",
            accessorKey: "image",
            enableColumnFilter: false,
            cell: ({ row }) => (
              <img
                src={row.original.image || "assets/images/no-image.png"}
                alt="Profile"
                style={{ width: 40, height: 40, borderRadius: "50%" }}
              />
            )
          },
          {
            header: 'Make Name',
            accessorKey: 'makeName',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Model Name',
            accessorKey: 'modelName',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Body Type',
            accessorKey: 'bodyType',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Vehicle Type',
            accessorKey: 'vehicleType',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'License Plate',
            accessorKey: 'licensePlate',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'In Service?',
            accessorKey: 'in_service',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Is Enable?',
            accessorKey: 'is_enable',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Action',
            accessorKey: 'action',
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }) => {
              if (!row.original || !row.original.id) {
                return <span className="text-muted">-</span>;
              }
              
              const vehicleId = row.original.id;
              const isUpdating = updatingVehicleId === vehicleId;
              
              return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedVehicle(row.original);
                      setSelectedTypeId(row.original.typeId || '');
                      setModalOpen(true);
                    }}
                    disabled={isUpdating}
                    style={{ 
                      minWidth: '80px',
                      minHeight: '32px'
                    }}
                  >
                    {isUpdating ? (
                      <span>
                        <i className="bx bx-loader bx-spin me-1"></i>
                        Updating...
                      </span>
                    ) : (
                      'Update'
                    )}
                  </button>
                </div>
              );
            },
          },
        ],
        [updatingVehicleId, handleUpdateVehicleType]
      );

    const totalRecords = data?.data?.length ?? 0;
    
    const transformedData = useMemo(() => {
      if (!data?.data) {
        return [];
      }
      
      return data.data.map(item => {
          const vehicleMedia = Array.isArray(item.vehicle_media) ? item.vehicle_media : [];

          return {
              id: item.id,
              image: vehicleMedia.length > 0 && vehicleMedia[0]?.url
                  ? vehicleMedia[0].url
                  : null,

              makeName: item.make_name || "N/A",
              modelName: item.model_name || "N/A",
              bodyType: item.body_type || "N/A",
              vehicleType: item.type_name || "N/A",
              typeId: item.type_id || null,
              licensePlate: item.license_plate || "N/A",
              in_service: item.in_service == 1 ? 'YES' : 'NO',
              is_enable: item.is_enable == 1 ? 'YES' : 'NO',
          };
      });
    }, [data]);
    
    return (
    <>
      {loading ? (
        <Skeleton count={10} height={40} />
      ) : (
        <>
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
          
          {/* Modal pour modifier le type de véhicule */}
          <Modal isOpen={modalOpen} toggle={toggleModal} centered>
            <ModalHeader toggle={toggleModal}>
              Update Vehicle Type
            </ModalHeader>
            <ModalBody>
              {selectedVehicle && (
                <div>
                  <div className="mb-3">
                    <label className="form-label"><strong>Vehicle:</strong></label>
                    <p className="mb-0">
                      {selectedVehicle.makeName} {selectedVehicle.modelName} - {selectedVehicle.licensePlate}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label"><strong>Current Vehicle Type:</strong></label>
                    <p className="mb-0">{selectedVehicle.vehicleType || 'N/A'}</p>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="vehicleTypeSelect" className="form-label">
                      <strong>Select New Vehicle Type:</strong>
                    </label>
                    <select
                      id="vehicleTypeSelect"
                      className="form-select"
                      value={selectedTypeId}
                      onChange={(e) => setSelectedTypeId(e.target.value)}
                      disabled={updatingVehicleId === selectedVehicle?.id}
                    >
                      <option value="">Select Vehicle Type</option>
                      {vehicleTypes.length > 0 ? vehicleTypes.map(type => {
                        const typeId = type.id;
                        const displayName = type.displayname || 'Unknown';
                        const vehicleType = type.vehicletype || '';
                        
                        return (
                          <option key={typeId} value={typeId}>
                            {displayName}{vehicleType && vehicleType !== displayName ? ` (${vehicleType})` : ''}
                          </option>
                        );
                      }) : (
                        <option disabled>No vehicle types available</option>
                      )}
                    </select>
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleModal} disabled={updatingVehicleId === selectedVehicle?.id}>
                Cancel
              </Button>
              <Button 
                color="primary" 
                onClick={handleModalSave}
                disabled={!selectedTypeId || updatingVehicleId === selectedVehicle?.id}
              >
                {updatingVehicleId === selectedVehicle?.id ? (
                  <>
                    <i className="bx bx-loader bx-spin me-1"></i>
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </>
  );
};

VehiclesTable.propTypes = {
  t: PropTypes.any
};

export default VehiclesTable;